import React, { useState } from "react";
import { MinimalCase } from "@/src/types/Entities/MinimalCase";
import { Conversation } from "@/src/types/Entities/Messaging/Conversation";
import Grid from "@mui/material/Grid";
import { Pagination, PaginationItem, Stack, TableHead, TableSortLabel } from "@mui/material";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import ConversationTableRow from "./ConversationTableRow";
import Button from "@mui/material/Button";
import { Link } from "react-router";
import { EmailOutlined } from "@mui/icons-material";
import { getNewMessageLink } from "@/src/services/messagingService";
import IncompleteCaseWarning from "./IncompleteCaseWarning";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import { caseMessagesStyle } from "./caseMessagesStyle";
import Icon from "@/src/components/Icon/Icon";
import { mergeSx } from "@/src/utils/mergeSx";
import dayjs from "dayjs";
import { translate } from "@/src/services/translations/translations";
import BackLink from "./BackLink";

type CaseMessagesProps = {
  selectedCase: MinimalCase;
  conversations: Conversation[];
};

const ITEMS_PER_PAGE = 20;

export default function CaseMessages({ selectedCase, conversations }: CaseMessagesProps) {
  const [page, setPage] = useState(0);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const filteredConversations = conversations.filter((conversation) => conversation.channel.caseId === selectedCase.id);
  const sortedConversations = [...filteredConversations].sort((a, b) => {
    const comparison = dayjs(a.latestMessageCreatedDate).valueOf() - dayjs(b.latestMessageCreatedDate).valueOf();
    return sortDirection === "desc" ? -comparison : comparison;
  });
  const paginatedConversations = sortedConversations.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

  return (
    <>
      <BackLink />
      <div id="case-messages" />
      <ContentBox headline={"Nachrichten"} headlineVariant={"h5"} showDivider={true}>
        <Typography sx={caseMessagesStyle.caseTitle}>
          {translate(selectedCase.name + ".label", {}, "")} {selectedCase.reference}
        </Typography>
        <IncompleteCaseWarning product={selectedCase} />
        {filteredConversations.length === 0 ? (
          <Stack sx={caseMessagesStyle.emptyState}>
            <Icon icon={({ fill }) => <EmailOutlined fontSize={"large"} sx={caseMessagesStyle.emptyIcon(fill)} />} variant={"bordered"} />
            <Typography variant={"h7"} component={"div"} sx={caseMessagesStyle.emptyTitle}>
              Keine Nachrichten für diesen Fall vorhanden
            </Typography>
            <Typography>
              Sobald wir Sie zu diesem Fall kontaktieren, finden Sie hier <br /> alle Nachrichten gebündelt.
            </Typography>
          </Stack>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell aria-hidden scope={"col"} sx={mergeSx(caseMessagesStyle.sortRowColumn, caseMessagesStyle.sortRowHidden)} />
                  <TableCell scope={"col"} sx={caseMessagesStyle.sortRowColumn} />
                  <TableCell align={"center"} scope={"col"} sx={mergeSx(caseMessagesStyle.sortRowColumn, caseMessagesStyle.sortRowDate)}>
                    <TableSortLabel active direction={sortDirection} onClick={() => setSortDirection((current) => (current === "asc" ? "desc" : "asc"))}>
                      {sortDirection === "desc" ? "Neueste zuerst" : "Älteste zuerst"}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedConversations.map((conversation) => (
                  <ConversationTableRow key={conversation.id} caseId={selectedCase.id} conversation={conversation} />
                ))}
              </TableBody>
            </Table>
            <Stack sx={caseMessagesStyle.pagination}>
              <Pagination
                aria-label="Seitennavigation Nachrichten"
                page={page + 1}
                count={Math.ceil(filteredConversations.length / ITEMS_PER_PAGE)}
                onChange={(_, nextPage) => setPage(nextPage - 1)}
                renderItem={(item) => <PaginationItem {...item} component={Link} to={"#case-messages"} replace />}
              />
            </Stack>
          </>
        )}
        <Grid container spacing={2} sx={caseMessagesStyle.actionGrid}>
          <Grid size={{ xs: 12, sm: 6 }} sx={caseMessagesStyle.actionEnd}>
            <Button component={Link} to={getNewMessageLink(selectedCase.name, selectedCase.id)} variant={"contained"} sx={caseMessagesStyle.actionLink}>
              Neue Nachricht senden
            </Button>
          </Grid>
        </Grid>
      </ContentBox>
    </>
  );
}
